import { Column } from '@app/components/data-grid/data-grid';
import { useAnalytics } from '@app/features/analytics/hooks/use-analytics';
import { useReadDashboardSharedState } from '@hopin-team/dashboard-shared-react';
import { useTranslation } from '@hopin-team/provider-translation';
import React, { useCallback, useEffect } from 'react';

import { DataGrid } from '../../components';
import { ErrorScreen } from '../../components/error-states/error-screen';
import { NoFilterResults } from '../../components/error-states/no-filter-results';
import { NoResults } from '../../components/error-states/no-results';
import { usePaginatedFetch } from '../../hooks/use-paginated-fetch';
import { useAnalyticsContext } from '../../providers/analytics-provider';
import { fetchVideos } from '../../services/endpoints';
import { InterHeaderStyles } from '../../styles';
import { formatDate, getNextPageCount } from '../../utils';
import { LoaderSkeleton } from './loader-skeleton';
import { VideoType } from './types';

const VideoThumbnail = ({ data }: { data: VideoType }) => {
  const { thumbnail_url, video_title } = data;
  return (
    <div className="w-full h-9 flex gap-2 justify-start items-center">
      <img
        src={thumbnail_url}
        alt={video_title}
        className="h-8 w-auto aspect-video object-contain"
      />
      <span title={video_title} className={InterHeaderStyles}>
        {video_title}
      </span>
    </div>
  );
};

const VideoViews = ({ data }: { data: VideoType }) => {
  const { times_watched } = data;
  return (
    <div className="w-full h-9 flex gap-2 justify-start items-center">
      <span title={`${times_watched}`}>
        {times_watched < 1000
          ? times_watched
          : `${Math.round((times_watched / 1000) * 10) / 10}k`}
      </span>
    </div>
  );
};

const WatchRatio = ({ data }: { data: VideoType }) => {
  const { watch_ratio } = data;
  return (
    <div className="w-full h-9 flex gap-2 justify-start items-center">
      <span>{`${Math.ceil(watch_ratio * 100)}%`}</span>
    </div>
  );
};

export const VideoColumns = (): Column<VideoType>[] => {
  const { t } = useTranslation();
  return [
    {
      text: t('analytics.video-name'),
      name: 'video_title',
      template: VideoThumbnail,
      width: '40%',
      customCSS: 'p-1.5 pr-6 pl-2',
    },
    {
      text: t('analytics.duration'),
      name: 'video_duration',
      width: '15%',
      customCSS: 'p-1.5 pr-6 pl-2',
      formatter: value => `${Math.round(parseInt(value) / 60)}`,
    },
    {
      text: t('analytics.views'),
      name: 'times_watched',
      width: '10%',
      template: VideoViews,
      customCSS: 'p-1.5 pr-6 pl-2',
    },
    {
      text: t('analytics.published'),
      name: 'published_at',
      width: '20%',
      customCSS: 'p-1.5 pr-6 pl-2',
      formatter: formatDate,
    },
    {
      text: t('analytics.avg-watched'),
      name: 'watch_ratio',
      width: '15%',
      customCSS: 'p-1.5 pr-6 pl-2',
      template: WatchRatio,
    },
  ];
};

export const Videos = () => {
  const { state, dispatch } = useAnalyticsContext();
  const orgId: string = useReadDashboardSharedState('org_id') || '';

  const config = {
    dateRange: state.dateRange,
    startDate: state.startDate,
    endDate: state.endDate,
    searchText: state.searchText,
    name: state.videoSortName,
    direction: state.videoSortDirection,
  };

  const {
    data,
    isLoading,
    isError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isInitialLoad,
  } = usePaginatedFetch(
    { ...config },
    `videos-${orgId}`,
    useCallback(fetchVideos, []),
  );
  const { track } = useAnalytics();

  const handleSort = (columnKey: string, direction: 'asc' | 'desc' | '') => {
    dispatch({
      type: 'update videos sort key and direction',
      payload: { name: columnKey, direction: direction },
    });
    track('ola_videos_tab_sorting_click', {
      columnKey: columnKey,
      direction: direction,
    });
  };

  const fetchMore = () => {
    if (!isLoading && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  };

  const result = {
    videosData: data?.pages.flatMap(page => page.videos) || [],
    pagination: data?.pages[data.pages.length - 1]?.pagination || {},
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <DataGrid<VideoType>
      columns={VideoColumns()}
      data={result.videosData || []}
      showLoader={(isLoading || isFetchingNextPage) && !isError}
      loader={LoaderSkeleton}
      loaderCount={getNextPageCount(result.pagination)}
      onSort={handleSort}
      fetchMore={fetchMore}
      sortConfig={{
        name: state.videoSortName as keyof VideoType,
        direction: state.videoSortDirection as '' | 'asc' | 'desc',
      }}
      noResults={() => (isInitialLoad ? NoResults : NoFilterResults)}
      error={props => <ErrorScreen {...props} showButton={true} />}
      isError={isError}
    />
  );
};
